import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import {useEffect, useState} from "react";

import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {PermissionsInfo, RequestInfo} from "@khan/shared-core";
import {preserveMarketingParams, useLocation} from "@khan/urls";

import PageRoot from "#app-shell-package/page-root.tsx";
import {useApolloGqlClient} from "#data-access/gql/use-apollo-gql-client.ts";
import useProfile from "#data-access/use-profile.ts";
import {isConsideredOver18} from "#shared-package/is-over-18.ts";
import SmartRedirect from "#shared/app-shell/smart-redirect.tsx";

import {
    fetchKhanmigoEligibilityData,
    type EligibilityData,
} from "./fetch-khanmigo-eligibility-data.ts";
import CannotDetermineEligibility from "./join-wizard/cannot-determine-eligibility.tsx";
import CannotStartCheckout from "./join-wizard/cannot-start-checkout.tsx";
import EnglishOnly from "./join-wizard/en-only.tsx";
import IneligibleCountry from "./join-wizard/ineligible-country.tsx";
import NoBirthdate from "./join-wizard/no-birthdate.tsx";
import UnderAgeGate from "./join-wizard/under-age-gate.tsx";
import {KhanmigoHeader} from "./khanmigo-header.tsx";
import {withStrings} from "./locales/khanmigo-checkout/load-strings.tsx";
import {getStripeCheckoutPrice} from "./pricing.ts";
import StripeCheckoutRedirect from "./stripe-checkout-redirect.tsx";

function KhanmigoCheckout() {
    const location = useLocation();
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    useOnMountEffect(() => {
        publishAnalyticsEvent({
            eventType: "AiGuideCheckoutStartedTI",
            eventSchemaVersion: 1,
        });
    });

    if (!PermissionsInfo.isLoggedIn) {
        const continueUrl = encodeURIComponent(
            preserveMarketingParams(
                "/khanmigo/checkout",
                new URLSearchParams(location.search),
            ),
        );

        const redirectUrl = preserveMarketingParams(
            `/signup?continue=${continueUrl}&referral=KhanmigoSignup`,
            new URLSearchParams(location.search),
        );

        return <SmartRedirect to={redirectUrl} />;
    }
    return (
        <PageRoot
            settings={{
                title: t`Get Khanmigo`,
                header: false,
                footer: false,
                noIndex: true,
            }}
        >
            <KhanmigoHeader />
            <ApplicableStep />
        </PageRoot>
    );
}

export default withStrings(KhanmigoCheckout);

enum EligibilityLoadingStatus {
    LOADING = 1,
    FAILED = 2,
    COMPLETE = 3,
}

function ApplicableStep() {
    const gqlClientConfig = useApolloGqlClient();
    const profileResult = useProfile();
    const location = useLocation();

    // Move outside component once ES/PT flag removed to avoid
    // rebuilding this object on every render
    const SUPPORTED_LOCALES = {
        en: true,
        es: true,
        pt: true,
    };

    const [eligibilityData, setEligibilityData] =
        useState<EligibilityData | null>(null);

    const [eligibilityDataStatus, setEligibilityDataStatus] = useState(
        EligibilityLoadingStatus.LOADING,
    );

    useEffect(() => {
        fetchKhanmigoEligibilityData(gqlClientConfig.gqlClient)
            .then((eligibilityData) => {
                setEligibilityData(eligibilityData);
                setEligibilityDataStatus(EligibilityLoadingStatus.COMPLETE);
            })
            .catch(() =>
                setEligibilityDataStatus(EligibilityLoadingStatus.FAILED),
            );
    }, [gqlClientConfig.gqlClient]);

    if (!SUPPORTED_LOCALES[RequestInfo.kaLocale.toLowerCase()]) {
        return (
            <EnglishOnly
                kaLocale={RequestInfo.kaLocale}
                countryCode={RequestInfo.remoteCountry}
            />
        );
    }

    // Grassroots Khanmigo is still only US
    if (RequestInfo.remoteCountry !== "US") {
        return (
            <IneligibleCountry
                kaLocale={RequestInfo.kaLocale}
                countryCode={RequestInfo.remoteCountry}
                product="grassroots"
            />
        );
    }

    if (eligibilityDataStatus === EligibilityLoadingStatus.LOADING) {
        return <LoadingIndicator />;
    }

    if (
        eligibilityDataStatus !== EligibilityLoadingStatus.COMPLETE ||
        !eligibilityData
    ) {
        return <CannotDetermineEligibility />;
    }

    if (eligibilityData.hasKhanmigoViaStripe) {
        return <SmartRedirect to="/settings/khanmigo" />;
    }

    if (profileResult.status === "loading") {
        return <LoadingIndicator />;
    }

    if (profileResult.status !== "success" || !profileResult.profile) {
        return <CannotDetermineEligibility />;
    }

    const profile = profileResult.profile;

    const notOfAge = !isConsideredOver18(profile);
    // @ts-expect-error - TS2339 - Property 'attributes' does not exist on type 'ProfileModelType'.
    const birthdateMissing = !profile.attributes["birthMonthYear"];

    if (notOfAge && birthdateMissing) {
        return <NoBirthdate underAgeGate={!!profile.get("underAgeGate")} />;
    }

    if (notOfAge) {
        return <UnderAgeGate age={eligibilityData.age || 0} />;
    }

    // preserve marketing params won't add the origin to the URL since it's a
    // relative path, so we need to add it ourselves.
    const cancelURL = preserveMarketingParams(
        new URL("/khanmigo", location.origin).toString(),
        new URLSearchParams(location.search),
    );

    const successURL = preserveMarketingParams(
        new URL("/khanmigo/thank-you", location.origin).toString(),
        new URLSearchParams(location.search),
    );

    return (
        // TODO(russell): To have this work locally, you need to be running
        // `make serve-fullstack` so the checkout session can be initialized
        // in dev mode. We should figure out how we can have this work with
        // `make serve-frontend` as well.
        <StripeCheckoutRedirect
            priceID={getStripeCheckoutPrice()}
            paymentFrequency="MONTHLY"
            // TODO(davemcalpine): MP-4202 create a cancel-specific page
            cancelURL={cancelURL}
            successURL={successURL}
            renderLoading={() => <LoadingIndicator />}
            renderError={() => <CannotStartCheckout />}
        />
    );
}

function LoadingIndicator() {
    return (
        <div className={css(styles.loadingView)}>
            <CircularSpinner />
        </div>
    );
}

const styles = StyleSheet.create({
    loadingView: {
        display: "flex",
        justifyContent: "center",
        alignContent: "center",
        margin: "20%",
    },
});
